/**
 * Tutorial progress bus — a tiny pub/sub decoupling the API client (which
 * knows the moment an action SUCCEEDS) from the interactive tutorial (which
 * wants to advance the moment the player actually does the step, not when
 * they merely open the page).
 *
 * Lives in its own module so api/client.ts and InteractiveTutorial.ts can
 * both import it without creating a circular dependency.
 */

export type TutorialSignal =
  | 'misdeed'
  | 'goodDeed'
  | 'train'
  | 'spar'
  | 'house'
  | 'inventory'
  | 'trade'
  | 'bank'
  | 'travel'
  | 'gather'
  | 'craft'
  | 'crypt'
  | 'race'
  | 'shout';

type Listener = (signal: TutorialSignal) => void;

const listeners = new Set<Listener>();

/** Subscribe to tutorial signals. Returns an unsubscribe function. */
export function onTutorialSignal(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Fire-and-forget — never throws, never blocks the caller. */
export function emitTutorialSignal(signal: TutorialSignal): void {
  for (const fn of listeners) {
    try {
      fn(signal);
    } catch {
      // A listener error must never break gameplay.
    }
  }
}
